import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import SEO, { breadcrumbJsonLd } from '../components/SEO';
import AcousticCamerasCompareTable from '../components/AcousticCamerasCompareTable';
import BoeingFellowTestimonial from '../components/BoeingFellowTestimonial';
import { AmmTaskFinder } from '../components/AmmTaskFinder';

const PRODUCTS = [
  {
    slug: 'h4mini',
    name: 'FOTRIC H4mini',
    tagline: 'Compact acoustic imaging — find leaks in seconds',
    mics: '112 MEMS microphones',
    range: '0.3 – 100 m',
    weight: '770 g · IP54',
    highlights: [
      'Handheld 770 g form factor',
      'Multiple image modes: single, multi, hologram',
      'On-device report generation',
      'Ideal for compressed air + tire + vacuum leaks',
    ],
    path: '/products/acoustic-cameras/h4mini',
    image:
      'https://static.wixstatic.com/media/0fa391_949dee3c5b9842539c8efd8cf74acd56~mv2.png/v1/fill/w_720,h_720,al_c,q_85,enc_auto/0fa391_949dee3c5b9842539c8efd8cf74acd56~mv2.png',
  },
  {
    slug: 'h-series',
    name: 'FOTRIC H-Series (H4 / H6)',
    tagline: 'High-performance dual-mode — leaks + partial discharge',
    mics: 'Up to 162 MEMS microphones',
    range: '0.3 – 200 m',
    weight: '1.3 kg · IP54',
    highlights: [
      'Available as H4 (compact) or H6 (flagship)',
      'Dual LQ + PD detection modes',
      'PRPD diagrams with auto-classification',
      'AnalyzIR professional software',
    ],
    path: '/products/acoustic-cameras/h-series',
    image:
      'https://static.wixstatic.com/media/571718_587034860df449119866a3f97c96fcce~mv2.png/v1/fill/w_720,h_547,al_c,q_85,enc_auto/571718_587034860df449119866a3f97c96fcce~mv2.png',
  },
  {
    slug: 'h-flex',
    name: 'FOTRIC H-Flex',
    tagline: '180° rotatable sensor array — ergonomic + arc-flash safe',
    mics: '162 MEMS microphones',
    range: '0.3 – 100 m',
    weight: 'NaviPdM® AI assistant',
    highlights: [
      'Industry-first 180° rotatable array',
      'Inspect overhead + ground-level without bending',
      'Maintain >90 cm from energized components',
      'AI-powered NaviPdM® workflow',
    ],
    path: '/products/acoustic-cameras/h-flex',
    image:
      'https://static.wixstatic.com/media/571718_b5167ab45dba4ec687063bb3e2d9a5cc~mv2.png/v1/fill/w_720,h_530,al_c,q_85,enc_auto/571718_b5167ab45dba4ec687063bb3e2d9a5cc~mv2.png',
  },
  {
    slug: 'h7',
    name: 'FOTRIC H7',
    tagline: 'Flagship long-range — 345 microphones, 300 m detection',
    mics: '345 MEMS microphones',
    range: '0.3 – 300 m',
    weight: 'NaviPdM® Venus AI',
    highlights: [
      'Highest mic density in a handheld',
      'Long-range detection to 984 ft',
      'AI PD diagnosis + leak cost evaluation',
      '6+ hour dual-battery runtime, dual-LED illumination',
    ],
    path: '/products/acoustic-cameras/h7',
    image:
      'https://static.wixstatic.com/media/0fa391_bab168322b7042a898cae58409a8922a~mv2.png/v1/fill/w_540,h_910,al_c,q_85,enc_auto/0fa391_bab168322b7042a898cae58409a8922a~mv2.png',
  },
];

const APPLICATIONS = [
  'Compressed air, bleed-air, and pneumatic leak detection',
  'Cabin pressurization inspections',
  'Hydraulic and fuel line acoustic surveys',
  'Landing gear strut nitrogen leak checks',
  'Partial discharge inspection of GPUs and GSE electrical gear',
  'Switchgear and transformer PD diagnostics in hangar electrical rooms',
  'Compressed air system energy audits',
  'Tire leak and loose-fastener vibration detection',
  'AOG response for hard-to-locate acoustic faults',
  'Fleet-wide PD trend monitoring',
];

const collectionJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'FOTRIC Acoustic Imaging Cameras for Aviation Maintenance',
  description:
    'Acoustic imaging cameras from FOTRIC for aviation MRO — H4mini, H-Series (H4/H6), H-Flex, and H7. Compare microphone density, detection range, and features to choose the right camera for compressed air leak detection and partial discharge inspection.',
  url: 'https://ctrl.aero/products/fotric',
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: PRODUCTS.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: p.name,
      url: `https://ctrl.aero${p.path}`,
    })),
  },
};

const FotricAcousticPage = ({ onRequestDemo, onRequestQuote }) => {
  const handleDemo = () => onRequestDemo && onRequestDemo({ product: 'Acoustic Cameras Overview' });
  const handleQuote = () => onRequestQuote && onRequestQuote({ product: 'Acoustic Cameras Overview' });

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Acoustic Imaging Cameras for Aviation Maintenance — ctrl.aero"
        description="Compare FOTRIC acoustic imaging cameras — H4mini, H-Series (H4/H6), H-Flex, and H7 — for aviation compressed air leak detection, partial discharge inspection, and MRO troubleshooting. Choose the right camera by microphone density, range, and workflow."
        canonical="https://ctrl.aero/products/fotric"
        jsonLd={[
          collectionJsonLd,
          breadcrumbJsonLd([
            { name: 'Home', url: 'https://ctrl.aero/' },
            { name: 'Solutions', url: 'https://ctrl.aero/#solutions' },
            { name: 'Acoustic Cameras', url: 'https://ctrl.aero/products/fotric' },
          ]),
        ]}
      />
      <Header onRequestDemo={handleDemo} onRequestQuote={handleQuote} />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 mb-8 transition-colors duration-200"
            data-testid="acoustic-hub-back-home"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Home</span>
          </Link>

          <div className="max-w-4xl">
            <span className="inline-block px-4 py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full text-blue-300 text-sm font-medium mb-4">
              Acoustic Imaging Cameras
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              Ultrasound You Can See — Purpose-Built for Aviation Maintenance
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 leading-relaxed mb-8">
              FOTRIC acoustic imaging cameras pair dense MEMS digital microphone arrays with a 13 MP optical
              camera to visualize ultrasonic sound in real time. Pinpoint compressed air and bleed-air leaks,
              cabin pressurization losses, hydraulic and fuel line faults, and partial discharges on aviation
              ground-support electrical equipment — all with a handheld camera. Choose the model that fits your
              range, workflow, and budget.
            </p>
            <div className="cta-group cta-group-compact max-w-5xl">
              <Button
                onClick={handleDemo}
                size="lg"
                data-testid="acoustic-hub-request-demo"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 text-sm sm:text-[15px] font-semibold"
              >
                Request Demo
              </Button>
              <Button
                onClick={handleQuote}
                size="lg"
                data-testid="acoustic-hub-request-quote"
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 text-sm sm:text-[15px] font-semibold"
              >
                Request Quote
              </Button>
              <a
                href="#compare"
                data-testid="acoustic-hub-jump-compare"
                className="text-sm sm:text-[15px] font-semibold rounded-md border border-white/30 text-white hover:bg-white/10 transition-colors"
              >
                Compare All Models
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Compare Grid */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e2d3b] mb-3">Choose Your Acoustic Camera</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-orange-500 mb-6"></div>
            <p className="text-lg text-slate-600 leading-relaxed">
              Four cameras, one platform. Every FOTRIC acoustic camera in the ctrl.aero line delivers the same
              core detection capability — the difference is microphone density, detection range, and workflow
              features. Explore each model in depth below.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {PRODUCTS.map((p) => (
              <Link
                key={p.slug}
                to={p.path}
                data-testid={`acoustic-hub-card-${p.slug}`}
                className="group bg-white rounded-3xl border-2 border-slate-100 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col"
              >
                <div className="aspect-[16/10] bg-gradient-to-br from-slate-100 to-white overflow-hidden flex items-center justify-center p-6">
                  {p.image ? (
                    <img
                      src={p.image}
                      alt={p.name}
                      className="max-h-56 w-auto object-contain group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <span className="text-slate-400 text-sm">Product image</span>
                  )}
                </div>
                <div className="p-6 sm:p-7 flex flex-col flex-grow">
                  <h3 className="text-2xl font-bold text-[#1e2d3b] mb-2 group-hover:text-blue-600 transition-colors">
                    {p.name}
                  </h3>
                  <p className="text-blue-600 font-medium mb-4">{p.tagline}</p>
                  <div className="grid grid-cols-3 gap-3 mb-5 text-center">
                    <div className="bg-slate-50 rounded-lg py-2 px-2">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Mics</p>
                      <p className="text-xs font-semibold text-[#1e2d3b] leading-tight">{p.mics}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg py-2 px-2">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Range</p>
                      <p className="text-xs font-semibold text-[#1e2d3b] leading-tight">{p.range}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg py-2 px-2">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Key</p>
                      <p className="text-xs font-semibold text-[#1e2d3b] leading-tight">{p.weight}</p>
                    </div>
                  </div>
                  <ul className="space-y-2 mb-5 flex-grow">
                    {p.highlights.map((h, i) => (
                      <li key={h} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-700 leading-snug">{h}</span>
                      </li>
                    ))}
                  </ul>
                  <span className="inline-flex items-center text-blue-600 font-semibold group-hover:text-orange-500 transition-colors">
                    Explore {p.name.replace('FOTRIC ', '')}
                    <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Boeing Technical Fellow standalone testimonial — surfaces the
          verbatim FOTRIC H6 quote above the compare table so buyers see
          the strongest social proof before they scroll into the specs. */}
      <BoeingFellowTestimonial onRequestQuote={onRequestQuote} />

      {/* Full comparison table (Apple-style) */}
      <AcousticCamerasCompareTable />
      <AmmTaskFinder tool="COM-18702" />

      {/* Applications */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e2d3b] mb-3">Aviation Maintenance Applications</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-orange-500 mb-6"></div>
            <p className="text-lg text-slate-600 leading-relaxed">
              Every FOTRIC acoustic camera in the ctrl.aero line is engineered for the demanding acoustic
              environments of aviation MRO — from the ramp to the hangar electrical room.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {APPLICATIONS.map((a, i) => (
              <div key={a.title || a} className="flex items-start gap-3 bg-slate-50 rounded-xl p-4">
                <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">{a}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Not Sure Which Camera Is Right?</h2>
          <p className="text-xl text-blue-100 mb-8 leading-relaxed">
            Book a 15-minute call with our aviation applications team. We'll match the right FOTRIC acoustic
            camera to your fleet, hangar workflow, and budget — no pressure, no forms.
          </p>
          <div className="cta-group cta-group-compact justify-center max-w-2xl mx-auto">
            <Button
              onClick={handleDemo}
              size="lg"
              data-testid="acoustic-hub-bottom-request-demo"
              className="bg-white text-blue-600 hover:bg-blue-50 px-6 text-sm sm:text-[15px] font-semibold"
            >
              Request Demo
            </Button>
            <Button
              onClick={handleQuote}
              size="lg"
              data-testid="acoustic-hub-bottom-request-quote"
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 text-sm sm:text-[15px] font-semibold"
            >
              Request Quote
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FotricAcousticPage;
